<template>
  <span
    v-bind="$attrs"
    :style="style"
    class="tw-relative tw-flex tw-min-w-0 tw-truncate"
    :class="{
      'tw-text-clip': props.hideEllipsis,
    }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <span
      class="tw-inline-block tw-truncate"
      :class="{
        [`${props.textClass || ''}`]: true,
      }"
    >
      <!-- eslint-disable vue/no-v-html -->
      <span v-if="!slots.default" v-html="sanitizedText"></span>
      <!-- eslint-enable vue/no-v-html -->
      <slot v-else />
    </span>
    <q-tooltip
      v-if="!props.disabled"
      v-bind="tooltipOptions"
      ref="tooltipEl"
      v-model="tooltipVisible"
      no-parent-event
    >
      <span class="tooltip-text tw-text-xs">
        {{ props.tooltipText || props.text }}
      </span>
    </q-tooltip>
  </span>
</template>

<script setup lang="ts">
import {
  computed,
  CSSProperties,
  useSlots,
  watch,
  ref,
  onUnmounted,
} from 'vue';
import DOMPurify from 'dompurify';
import { QTooltip, QTooltipProps } from 'quasar';

interface Props {
  text?: string;
  tooltipText?: string; // if different from text
  maxWidth?: string;
  tooltipProps?: Partial<QTooltipProps>;
  hideEllipsis?: boolean;
  injectHtml?: boolean;
  disabled?: boolean;
  textClass?: string;
}
const props = withDefaults(defineProps<Props>(), {
  maxWidth: '100%',
  tooltipProps: () => ({}),
  hideEllipsis: false,
  injectHtml: false,
  disabled: false,
});

const slots = useSlots() as Record<string, unknown>;

const tooltipEl = ref<QTooltip | null>(null);
const tooltipVisible = ref(false);
let hoverTimeoutId: ReturnType<typeof setTimeout> | null = null;

const handleMouseEnter = () => {
  if (props.disabled) return;

  hoverTimeoutId = setTimeout(() => {
    tooltipVisible.value = true;
    hoverTimeoutId = null;
  }, 300);
};

const handleMouseLeave = () => {
  if (hoverTimeoutId) {
    clearTimeout(hoverTimeoutId);
    hoverTimeoutId = null;
  }
  tooltipVisible.value = false;
};

// Clean up pending timeout when component unmounts
onUnmounted(() => {
  if (hoverTimeoutId) {
    clearTimeout(hoverTimeoutId);
    hoverTimeoutId = null;
  }
});

const tooltipOptions = computed(() => {
  return {
    anchor: 'top middle',
    self: 'bottom middle',
    ...props.tooltipProps,
  } as Partial<QTooltipProps>;
});

const style = computed<CSSProperties>(() => {
  return {
    maxWidth: props.maxWidth || '100%',
  };
});

const sanitizedText = computed(() => {
  if (!props.injectHtml) return props.text;
  return DOMPurify.sanitize(props.text);
});

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled && tooltipEl.value) {
      tooltipEl.value.hide();
    }
  }
);
</script>

<style lang="scss" scoped>
.tooltip-text {
  display: block;
  max-width: 300px;
}
</style>
